 
import {
  GeneralError,
  LogoutUseCase,
  ResetPasswordUseCase,
} from '51trips-frontend-core'
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { take } from 'rxjs/operators'

import CameraIcon from '../../assets/icons/camera.svg?react'
import UserIcon from '../../assets/icons/user.svg?react'
import { supportedLanguages } from '../../constants/supportedLanguages'
import { userReadableDescription } from '../../utils/UserReadableDescription'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Select } from '../Select'
import {AuthenticationRepository} from "../../repositories/AuthenticationRepository.ts";
import {useSelector} from "react-redux";

const supportedLanguagesOptions = Object.entries(supportedLanguages).map(([value, label]) => ({
  value,
  label
}))

const logoutUseCase = new LogoutUseCase(new AuthenticationRepository())

const resetPasswordUseCase = new ResetPasswordUseCase(new AuthenticationRepository())

export const ProfileDialog = forwardRef<React.ElementRef<typeof Dialog<boolean>>>((_, ref) => {
  /// Navigation

  const navigate = useNavigate()

  /// i18m
  const { t, i18n } = useTranslation()


  /// Refs

  const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
  const changePasswordDialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
  const languageDialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)

  const user = useSelector((state) => state.auth.user)

  /// States

  const [currentLanguage, setCurrentLanguage] = useState(i18n.language)








  /// Hookes

  useImperativeHandle(
    ref,
    () => ({
      open: dialog.current!.open,
      close: dialog.current!.close
    }),
    []
  )

  /// Actions

  const openChangePasswordDialog = (e: React.MouseEvent) => {
    e.preventDefault()

    changePasswordDialog.current?.open()
  }

  const openLanguageDialog = (e: React.MouseEvent) => {
    e.preventDefault()

    setCurrentLanguage(i18n.language)

    languageDialog.current?.open()
  }

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault()

    logoutUseCase
      .logout()
      .pipe(take(1))
      .subscribe({
        next: () => {
          navigate('/auth/login')
        },
        error: (error: GeneralError) => {
          userReadableDescription(error)
        }
      })
  }


  const submitCurrentLanguage = () => {
    i18n.changeLanguage(currentLanguage)
    //   localStorage.setItem('i18nextLng', currentLanguage)
    languageDialog.current?.close()
  }

  const sendChangePasswordRequest = () => {
    if (user?.email) {
      resetPasswordUseCase
        .resetPassword(user.email)
        .pipe(take(1))
        .subscribe({
          next: () => {
            changePasswordDialog.current?.close()
          },
          error: (error: GeneralError) => {
            userReadableDescription(error)
          }
        })
    } else {
      console.error('User is not loaded')
    }
  }



  /// Render

  return (
    <Dialog
      title={t('title', { ns: 'profile' })}
      ref={dialog}
      width="wide"
      withHeaderDelimiter
      withCloseButton
    >
      <div className="border-b border-neutralBlack-300 pb-8 mb-8 flex items-center gap-6">
        <div className="size-32 rounded-full relative border-[1.5px] border-neutralBlack-300">
          {user?.corporateLogo ? (
            <img
              src={user.corporateLogo}
              className="w-full h-full object-contain rounded-full p-2"
            />
          ) : (
            <UserIcon className="object-contain m-4" />
          )}

          <button
            className="hidden outline-none size-8 md:size-12 flex absolute bottom-0 right-0 rounded-full bg-neutralBlack-300 hover:brightness-105"
          >
            <CameraIcon className="m-auto size-6 md:size-8" />
          </button>
        </div>

        <div className="text-[25px]  font-medium rounded-xl bg-neutralBlack-50 px-[9px] py-[6px]">
          {user.name}


        </div>
      </div>

      <div className="space-y-6">

        <div className="space-y-4">
          <div className="font-medium">{t('myAccount', { ns: 'profile' })}</div>

          <div>
            <a href="" className="hover:underline" onClick={openChangePasswordDialog}>
              {t('changePassword', { ns: 'profile' })}
            </a>
          </div>

          <div>
            <a href="" className="hover:underline" onClick={openLanguageDialog}>
              {t('language', { ns: 'profile' })}
            </a>
          </div>

          <div>
            <a href="" className="hover:underline" onClick={handleLogout}>
              {t('logout', { ns: 'profile' })}
            </a>
          </div>
        </div>
      </div>

      {/* Change password dialog */}

      <Dialog ref={changePasswordDialog} withCloseButton>
        <div className="text-center space-y-6 pt-12 pb-8">
          <div className="text-2xl font-semibold">
            {t('changePasswordTitle', { ns: 'profile' })}
          </div>

          <div>{t('changePasswordDescription', { ns: 'profile', email: user?.email })}</div>

          <Button
            variant="primary"
            label={t('sendMeLink', { ns: 'profile' })}
            onClick={sendChangePasswordRequest}
          />
        </div>
      </Dialog>

      {/* Change language dialog */}

      <Dialog ref={languageDialog} withCloseButton>
        <div className="text-center space-y-6 pt-12 pb-8">
          <div className="text-2xl font-semibold">{t('language', { ns: 'profile' })}</div>

          <div>{t('languageDescription', { ns: 'profile' })}</div>

          <div>
            <Select
              value={currentLanguage}
              options={supportedLanguagesOptions}
              onChange={(e) => setCurrentLanguage(e.target.value)}
              className="w-3/4"
            />
          </div>

          <Button
            variant="primary"
            label={t('save', { ns: 'profile' })}
            onClick={submitCurrentLanguage}
          />
        </div>
      </Dialog>

      {/* Upload photo dialog */}


    </Dialog>
  )
})
